import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getBudgetUploadConfigUsers,
    saveBudgetViewUploadAccessDetails,
    viewBudgetViewAttachmentConfigDetails,
} from '../../api/businessinfoAPI/budgetAPI';

// ── Async Thunks ───────────────────────────────────────────────────────────────

const normalizeUserList = (raw) => {
    const list = Array.isArray(raw?.Data) ? raw.Data : Array.isArray(raw) ? raw : [];
    return list.map(u => ({
        userId:   String(u.Userid ?? u.UserId ?? u.UserID ?? ''),
        userName: u.UserName ?? u.Username ?? '',
    }));
};

const normalizeAccessList = (raw) => {
    const list = Array.isArray(raw?.Data) ? raw.Data : Array.isArray(raw) ? raw : [];
    return list.map(u => ({
        userId:   String(u.Userid ?? u.UserId ?? u.UserID ?? ''),
        userName: u.UserName ?? u.Username ?? '',
        userRole: u.UserRole ?? u.RoleName ?? u.Role ?? '',
    }));
};

export const fetchBudgetViewAccessList = createAsyncThunk(
    'budgetViewAttachmentConfig/fetchBudgetViewAccessList',
    async (_, { rejectWithValue }) => {
        try {
            const res = await viewBudgetViewAttachmentConfigDetails();
            return normalizeAccessList(res);
        } catch (err) {
            return rejectWithValue(err?.Message || err?.message || 'Failed to load current access list');
        }
    }
);

export const fetchAddableUsers = createAsyncThunk(
    'budgetViewAttachmentConfig/fetchAddableUsers',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getBudgetUploadConfigUsers('Add');
            return normalizeUserList(res);
        } catch (err) {
            return rejectWithValue(err?.Message || err?.message || 'Failed to load employees available to add');
        }
    }
);

export const fetchRemovableUsers = createAsyncThunk(
    'budgetViewAttachmentConfig/fetchRemovableUsers',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getBudgetUploadConfigUsers('Remove');
            return normalizeUserList(res);
        } catch (err) {
            return rejectWithValue(err?.Message || err?.message || 'Failed to load employees with existing access');
        }
    }
);

export const saveBudgetViewAttachmentAccess = createAsyncThunk(
    'budgetViewAttachmentConfig/saveBudgetViewAttachmentAccess',
    async ({ actionType, userId, userName, createdBy }, { rejectWithValue }) => {
        try {
            const res = await saveBudgetViewUploadAccessDetails({
                ConfigType: actionType,
                UserId:     userId,
                Username:   userName,
                Createdby:  createdBy,
            });
            const data = res?.Data;
            const status = typeof data === 'string' ? data : (res?.Message || '');
            if (status && status.toLowerCase() !== 'successfull' && status.toLowerCase() !== 'success') {
                return rejectWithValue(status);
            }
            return { actionType, userId, userName };
        } catch (err) {
            return rejectWithValue(err?.Message || err?.message || 'Failed to save access change');
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    addableUsers:   [],
    removableUsers: [],
    accessList:     [],
    saveResult:     null,

    loading: {
        addable:   false,
        removable: false,
        access:    false,
        save:      false,
    },
    errors: {
        addable:   null,
        removable: null,
        access:    null,
        save:      null,
    },
};

const budgetViewAttachmentConfigSlice = createSlice({
    name: 'budgetViewAttachmentConfig',
    initialState,
    reducers: {
        clearBudgetViewAttachmentSaveResult: (state) => {
            state.saveResult = null;
            state.errors.save = null;
        },
        resetBudgetViewAttachmentConfig: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAddableUsers.pending, s => { s.loading.addable = true; s.errors.addable = null; })
            .addCase(fetchAddableUsers.fulfilled, (s, a) => { s.loading.addable = false; s.addableUsers = a.payload; })
            .addCase(fetchAddableUsers.rejected, (s, a) => { s.loading.addable = false; s.errors.addable = a.payload; });

        builder
            .addCase(fetchRemovableUsers.pending, s => { s.loading.removable = true; s.errors.removable = null; })
            .addCase(fetchRemovableUsers.fulfilled, (s, a) => { s.loading.removable = false; s.removableUsers = a.payload; })
            .addCase(fetchRemovableUsers.rejected, (s, a) => { s.loading.removable = false; s.errors.removable = a.payload; });

        builder
            .addCase(fetchBudgetViewAccessList.pending, s => { s.loading.access = true; s.errors.access = null; })
            .addCase(fetchBudgetViewAccessList.fulfilled, (s, a) => { s.loading.access = false; s.accessList = a.payload; })
            .addCase(fetchBudgetViewAccessList.rejected, (s, a) => { s.loading.access = false; s.errors.access = a.payload; });

        builder
            .addCase(saveBudgetViewAttachmentAccess.pending, s => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(saveBudgetViewAttachmentAccess.fulfilled, (s, a) => {
                s.loading.save = false;
                s.saveResult = a.payload;
            })
            .addCase(saveBudgetViewAttachmentAccess.rejected, (s, a) => { s.loading.save = false; s.errors.save = a.payload; });
    },
});

export const {
    clearBudgetViewAttachmentSaveResult,
    resetBudgetViewAttachmentConfig,
} = budgetViewAttachmentConfigSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectAddableUsers      = s => s.budgetViewAttachmentConfig.addableUsers;
export const selectAddableLoading    = s => s.budgetViewAttachmentConfig.loading.addable;
export const selectAddableError      = s => s.budgetViewAttachmentConfig.errors.addable;
export const selectRemovableUsers    = s => s.budgetViewAttachmentConfig.removableUsers;
export const selectRemovableLoading  = s => s.budgetViewAttachmentConfig.loading.removable;
export const selectRemovableError    = s => s.budgetViewAttachmentConfig.errors.removable;
export const selectAccessList        = s => s.budgetViewAttachmentConfig.accessList;
export const selectAccessLoading     = s => s.budgetViewAttachmentConfig.loading.access;
export const selectAccessError       = s => s.budgetViewAttachmentConfig.errors.access;
export const selectSaveLoading       = s => s.budgetViewAttachmentConfig.loading.save;
export const selectSaveError         = s => s.budgetViewAttachmentConfig.errors.save;
export const selectSaveResult        = s => s.budgetViewAttachmentConfig.saveResult;

export default budgetViewAttachmentConfigSlice.reducer;
